import React, { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { C, fmtINR } from "./ipd-billing/constants";
import SvgIcon from "./ipd-billing/SvgIcon";
import IPDBillingLayout from "./ipd-billing/components/IPDBillingLayout";
import PatientSearchBar from "./ipd-billing/components/PatientSearchBar";
import {
  resolveAdmissionId,
  persistAdmissionContext,
  restoreAdmissionContext,
  hydrateRestoredPatient,
  clearAdmissionContext,
  listAllocatedAdmissions,
  admissionToPatientContext,
  getLedgerAudit,
  mapLedgerEntry,
} from "./ipd-billing/billing-api";

// Filter tab → API `entry_filter` value. Cards always show full-encounter totals;
// only the entries table respects the filter (server-side).
const FILTER_TABS = [
  { key: "all",        label: "All Entries",  entryFilter: "all" },
  { key: "debit",      label: "Debits",       entryFilter: "debits" },
  { key: "credit",     label: "Credits",      entryFilter: "credits" },
  { key: "adjustment", label: "Adjustments",  entryFilter: "adjustments" },
];

function toAmount(v) {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

// ─── helpers ──────────────────────────────────────────────────────────────────
function balanceColor(n) {
  if (n == null) return C.textSub;
  if (n < 0) return C.danger;
  if (n === 0) return C.textSub;
  return C.text;
}

function KpiCard({ label, value, valueColor, sub, icon, iconBg }) {
  return (
    <div
      style={{
        background: C.card,
        border: `1px solid ${C.border}`,
        borderRadius: 10,
        padding: "18px 22px",
        display: "flex",
        alignItems: "center",
        gap: 16,
        flex: 1,
        minWidth: 180,
      }}
    >
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: 10,
          background: iconBg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <SvgIcon name={icon} size={20} color={C.card} />
      </div>
      <div>
        <div style={{ fontSize: 11, fontWeight: 600, color: C.textSub, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 4 }}>
          {label}
        </div>
        <div style={{ fontSize: 22, fontWeight: 700, color: valueColor || C.text, lineHeight: 1 }}>
          {value}
        </div>
        {sub && (
          <div style={{ fontSize: 11, color: C.textSub, marginTop: 3 }}>{sub}</div>
        )}
      </div>
    </div>
  );
}

// ─── main component ───────────────────────────────────────────────────────────
export default function IPDBillingLedger() {
  const location      = useLocation();
  const passedPatient = location.state?.patient || null;

  // ── Patient selection (same dropdown as Charge Capture & Invoice) ───────────
  const [selectedPatient,   setSelectedPatient]   = useState(() => {
    if (passedPatient?.admission_id || passedPatient?.admissionId) return passedPatient;
    return restoreAdmissionContext();
  });
  const [admissions,        setAdmissions]        = useState([]);
  const [searchQuery,       setSearchQuery]       = useState("");
  const [loadingAdmissions, setLoadingAdmissions] = useState(false);
  const [searchError,       setSearchError]       = useState("");

  const admissionId = resolveAdmissionId(selectedPatient) || "";
  const encounterId = selectedPatient?.encounter_id || admissionId || "";
  const patientName = selectedPatient?.name || "—";

  const loadAdmissions = useCallback(async () => {
    setLoadingAdmissions(true);
    setSearchError("");
    try {
      const list = await listAllocatedAdmissions("Allocated");
      setAdmissions(list);
    } catch (err) {
      console.error("Admissions fetch error:", err);
      setSearchError(err.message || "Failed to load patients");
      setAdmissions([]);
    } finally {
      setLoadingAdmissions(false);
    }
  }, []);

  useEffect(() => {
    loadAdmissions();
  }, [loadAdmissions]);

  // Fill in an id-only restore (name/bed/doctor/payer) once the list lands.
  useEffect(() => {
    const hydrated = hydrateRestoredPatient(selectedPatient, admissions);
    if (hydrated !== selectedPatient) {
      persistAdmissionContext(hydrated);
      setSelectedPatient(hydrated);
    }
  }, [admissions, selectedPatient]);

  useEffect(() => {
    if (passedPatient) {
      const id = resolveAdmissionId(passedPatient);
      if (id) {
        persistAdmissionContext(passedPatient);
        setSelectedPatient(passedPatient);
      }
    }
  }, [passedPatient]);

  const handleSelectAdmission = useCallback((row) => {
    const patient = admissionToPatientContext(row);
    persistAdmissionContext(patient);
    setSelectedPatient(patient);
    setSearchQuery("");
  }, []);

  const handleClearPatient = useCallback(() => {
    setSelectedPatient(null);
    clearAdmissionContext();
    setSearchQuery("");
    setLedger(null);
  }, []);

  // ── Ledger data (GET .../billing/ledger-audit/) ─────────────────────────────
  const [ledger,  setLedger]  = useState(null);      // raw response
  const [entries, setEntries] = useState(null);      // mapped rows (null = loading)
  const [filter,  setFilter]  = useState("all");     // all | debit | credit | adjustment
  const [loadError, setLoadError] = useState("");

  const loadLedger = useCallback(async () => {
    if (!admissionId) {
      setLedger(null);
      setEntries(null);
      return;
    }
    setEntries(null);
    setLoadError("");
    try {
      const entryFilter = FILTER_TABS.find((t) => t.key === filter)?.entryFilter || "all";
      const res = await getLedgerAudit(admissionId, entryFilter);
      setLedger(res || null);
      const rows = Array.isArray(res?.entries) ? res.entries : [];
      setEntries(rows.map(mapLedgerEntry));
    } catch (err) {
      console.error("Ledger fetch error:", err);
      setLoadError(err.message || "Failed to load ledger");
      setLedger(null);
      setEntries([]);
    }
  }, [admissionId, filter]);

  useEffect(() => {
    loadLedger();
  }, [loadLedger]);

  const isLoading = entries === null;
  const filteredEntries = entries || [];

  // Summary cards always reflect the full encounter (independent of the filter).
  const cards = ledger?.cards || {};
  const debits  = toAmount(cards.debits);
  const credits = toAmount(cards.credits);
  const netOutstanding = toAmount(cards.net_outstanding);
  const lastActivityAt = cards.last_activity_at;
  const lastActivity = lastActivityAt
    ? (() => {
        const d = new Date(lastActivityAt);
        return Number.isNaN(d.getTime())
          ? String(lastActivityAt)
          : d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false });
      })()
    : "—";
  const lastActivityBy = ledger?.last_activity_by || (entries && entries[0]?.by) || "";

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <IPDBillingLayout activeNav="ledger">
      <style>{`
        .ledger-row:hover { background: #f8f9fa !important; }
        .ledger-tab:hover { color: ${C.primary} !important; }
        .export-btn:hover { background: ${C.primary} !important; color: #fff !important; }
      `}</style>

      <div style={{ padding: "28px 32px", display: "flex", flexDirection: "column", gap: 20 }}>

        {/* ── patient selection ───────────────────────────────────────────── */}
        <PatientSearchBar
          admissions={admissions}
          loadingAdmissions={loadingAdmissions}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onSelect={handleSelectAdmission}
          selectedPatient={selectedPatient}
          onClear={handleClearPatient}
          onRefresh={loadAdmissions}
          searchError={searchError}
          selectHint="select one to view the ledger"
        />

        {selectedPatient && (
        <>
        {/* ── page header ─────────────────────────────────────────────────── */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: C.text }}>
              Billing Ledger &amp; Audit Trail
            </h2>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: C.textSub }}>
              Full chronological financial history · {patientName} · Encounter {encounterId}
            </p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 12px",
                borderRadius: 20,
                background: "#d1f2db",
                color: "#145a32",
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              <SvgIcon name="lock" size={13} color="#145a32" />
              Tamper-evident
            </span>
            <button
              className="export-btn"
              onClick={() => {
                // API: POST /api/ipd-billing/export_ipd_ledger/
                // Body: { admission_id: admissionId, format: "csv" }
                alert("Export ready — integrate with API");
              }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 7,
                padding: "8px 16px",
                border: `1.5px solid ${C.primary}`,
                borderRadius: 8,
                background: "transparent",
                color: C.primary,
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                transition: "background 0.15s, color 0.15s",
              }}
            >
              <SvgIcon name="export" size={15} color="inherit" />
              Export Ledger
            </button>
          </div>
        </div>

        {/* ── KPI row ──────────────────────────────────────────────────────── */}
        {isLoading ? (
          <div style={{ display: "flex", gap: 16 }}>
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                style={{
                  flex: 1,
                  height: 90,
                  borderRadius: 10,
                  background: "linear-gradient(90deg, #e9ecef 25%, #f8f9fa 50%, #e9ecef 75%)",
                  backgroundSize: "200% 100%",
                  animation: "ipdShimmer 1.4s infinite",
                }}
              />
            ))}
          </div>
        ) : (
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            <KpiCard
              label="Debits"
              value={fmtINR(debits ?? 0)}
              valueColor={C.text}
              icon="trending"
              iconBg={C.primary}
            />
            <KpiCard
              label="Credits"
              value={fmtINR(credits ?? 0)}
              valueColor={C.success}
              icon="check"
              iconBg={C.success}
            />
            <KpiCard
              label="Net Outstanding"
              value={fmtINR(netOutstanding ?? 0)}
              valueColor={(netOutstanding ?? 0) > 0 ? C.danger : C.success}
              icon="wallet"
              iconBg={(netOutstanding ?? 0) > 0 ? C.danger : C.success}
            />
            <KpiCard
              label="Last Activity"
              value={lastActivity}
              sub={lastActivityBy ? `today · ${lastActivityBy}` : undefined}
              icon="clock"
              iconBg="#6c757d"
            />
          </div>
        )}

        {/* ── ledger table card ────────────────────────────────────────────── */}
        <div
          style={{
            background: C.card,
            border: `1px solid ${C.border}`,
            borderRadius: 12,
          }}
        >
          {/* card header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "16px 20px",
              borderBottom: `1px solid ${C.border}`,
              flexWrap: "wrap",
              gap: 12,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <SvgIcon name="clock" size={18} color={C.primary} />
              <span style={{ fontWeight: 600, color: C.text, fontSize: 15 }}>Ledger Entries</span>
              {!isLoading && (
                <span
                  style={{
                    marginLeft: 4,
                    padding: "2px 8px",
                    borderRadius: 12,
                    background: C.accent,
                    color: C.primary,
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                >
                  {filteredEntries.length}
                </span>
              )}
            </div>

            {/* filter tabs */}
            <div style={{ display: "flex", gap: 4 }}>
              {FILTER_TABS.map((tab) => (
                <button
                  key={tab.key}
                  className="ledger-tab"
                  onClick={() => setFilter(tab.key)}
                  style={{
                    padding: "5px 12px",
                    borderRadius: 6,
                    border: filter === tab.key ? `1.5px solid ${C.primary}` : `1.5px solid ${C.border}`,
                    background: filter === tab.key ? C.accent : "transparent",
                    color: filter === tab.key ? C.primary : C.textSub,
                    fontSize: 12,
                    fontWeight: filter === tab.key ? 600 : 400,
                    cursor: "pointer",
                    transition: "all 0.15s",
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* table */}
          {isLoading ? (
            <div style={{ padding: 20 }}>
              {[1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  style={{
                    height: 44,
                    borderRadius: 6,
                    marginBottom: 10,
                    background: "linear-gradient(90deg, #e9ecef 25%, #f8f9fa 50%, #e9ecef 75%)",
                    backgroundSize: "200% 100%",
                    animation: "ipdShimmer 1.4s infinite",
                  }}
                />
              ))}
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "#f8f9fa" }}>
                    {[
                      { label: "TIMESTAMP",   align: "left"  },
                      { label: "DESCRIPTION", align: "left"  },
                      { label: "DEBIT",       align: "right" },
                      { label: "CREDIT",      align: "right" },
                      { label: "BALANCE",     align: "right" },
                      { label: "ADDED BY",    align: "left"  },
                    ].map((col) => (
                      <th
                        key={col.label}
                        style={{
                          padding: "10px 16px",
                          textAlign: col.align,
                          fontSize: 11,
                          fontWeight: 600,
                          color: C.textSub,
                          letterSpacing: "0.06em",
                          borderBottom: `1px solid ${C.border}`,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredEntries.length === 0 ? (
                    <tr>
                      <td colSpan={6} style={{ padding: 40, textAlign: "center", color: loadError ? C.danger : C.textSub }}>
                        {loadError || "No entries for this filter."}
                      </td>
                    </tr>
                  ) : (
                    filteredEntries.map((e, idx) => (
                      <tr
                        key={e.id}
                        className="ledger-row"
                        style={{
                          background: idx % 2 === 0 ? "#fff" : "#fafafa",
                          transition: "background 0.12s",
                        }}
                      >
                        {/* TIMESTAMP */}
                        <td style={{ padding: "12px 16px", whiteSpace: "nowrap", color: C.textSub }}>
                          <span style={{ fontWeight: 600, color: C.text }}>{e.date}</span>
                          {" "}
                          <span style={{ fontSize: 11 }}>{e.time}</span>
                        </td>

                        {/* DESCRIPTION */}
                        <td style={{ padding: "12px 16px", color: C.text }}>
                          {e.description}
                        </td>

                        {/* DEBIT */}
                        <td
                          style={{
                            padding: "12px 16px",
                            textAlign: "right",
                            fontWeight: 600,
                            color: e.debit != null ? C.danger : C.textSub,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {e.debit != null ? fmtINR(e.debit) : "—"}
                        </td>

                        {/* CREDIT */}
                        <td
                          style={{
                            padding: "12px 16px",
                            textAlign: "right",
                            fontWeight: 600,
                            color: e.credit != null ? C.success : C.textSub,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {e.credit != null ? fmtINR(e.credit) : "—"}
                        </td>

                        {/* BALANCE */}
                        <td
                          style={{
                            padding: "12px 16px",
                            textAlign: "right",
                            fontWeight: 700,
                            color: balanceColor(e.balance),
                            whiteSpace: "nowrap",
                          }}
                        >
                          {fmtINR(e.balance)}
                        </td>

                        {/* ADDED BY */}
                        <td style={{ padding: "12px 16px" }}>
                          <span
                            style={{
                              fontFamily: "monospace",
                              fontSize: 12,
                              color: C.textSub,
                            }}
                          >
                            {e.by}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ── audit footer ─────────────────────────────────────────────────── */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "12px 16px",
            borderRadius: 8,
            background: "#f0f4f8",
            border: `1px solid ${C.border}`,
            fontSize: 12,
            color: C.textSub,
          }}
        >
          <SvgIcon name="lock" size={14} color={C.textSub} />
          All ledger entries are hash-chained and tamper-evident. Any modification is logged with operator ID, timestamp, and reason.
        </div>
        </>
        )}


      </div>
    </IPDBillingLayout>
  );
}
